import { IFeatureChip } from "../interfaces/i-feature-chip";
import { flattenObject, pathToLabel } from "./property-object-flattener";
import { FIELD_OVERRIDES } from "../config/property-field-overrides.config";

/**
 * Builds IFeatureChip[] by scanning all `.detected` leaf paths in an object.
 *
 * - Parent paths whose children also contain `.detected` are skipped automatically
 *   (e.g. pool.detected is omitted when pool.types.*.detected entries exist).
 * - Label resolved in priority order:
 *     1. labelMap[parentPath]
 *     2. FIELD_OVERRIDES[prefix.parentPath]?.label
 *     3. pathToLabel(lastSegment)  ← auto fallback
 * - Detail auto-populated from sibling .area (formatted), .count, or .percentage.
 *
 * @param obj        Sub-object to scan (e.g. property_features, structure_features).
 * @param prefix     Path prefix for FIELD_OVERRIDES lookup (e.g. "structure_features").
 * @param labelMap   Explicit label overrides: parentPath → chip label (highest priority).
 * @param formatArea Optional area formatter for unit-dependent display.
 */
export function buildDetectedChips(
  obj: Record<string, any>,
  prefix = "",
  labelMap: Record<string, string> = {},
  formatArea?: (m2: number) => string,
): IFeatureChip[] {
  if (!obj) return [];

  const flat = flattenObject(obj);
  const detectedPaths = Object.keys(flat).filter((p) => p.endsWith(".detected"));

  return detectedPaths
    .filter((path) => {
      const parentPath = path.replace(/\.detected$/, "");
      // Skip if a deeper sub-path also ends in .detected (this is a parent with subtypes)
      return !detectedPaths.some((p) => p !== path && p.startsWith(`${parentPath}.`));
    })
    .map((path) => {
      const parentPath = path.replace(/\.detected$/, "");
      const lastSegment = parentPath.split(".").pop()!;
      const overridePath = prefix ? `${prefix}.${parentPath}` : parentPath;

      const label =
        labelMap[parentPath] ?? FIELD_OVERRIDES[overridePath]?.label ?? pathToLabel(lastSegment);

      const area: number | null = flat[`${parentPath}.area`] ?? null;
      const count: number | null = flat[`${parentPath}.count`] ?? null;
      const percentage: number | null = flat[`${parentPath}.percentage`] ?? null;

      const detail =
        area != null && formatArea
          ? formatArea(area)
          : count != null
            ? String(count)
            : percentage != null
              ? `${percentage}%`
              : undefined;

      return { label, detected: !!flat[path], area, count, detail };
    });
}

/**
 * Builds IFeatureChip[] from count-based objects (no .detected boolean needed).
 * Supports two structural patterns:
 *   - Simple:   { ac_unit: { count: N, area?: M } }  → one chip per key
 *   - Subtypes: { solar_panel: { photovoltaic: { count: N, area?: M } } }
 *               → one chip per subtype with label = parent key, type = subtype key
 *
 * Detection is inferred from count > 0 or area > 0.
 *
 * @param obj        Sub-object to scan (e.g. roof_elements).
 * @param prefix     Path prefix for FIELD_OVERRIDES lookup (e.g. "roof_elements").
 * @param labelMap   Explicit label overrides: key → chip label.
 * @param formatArea Optional area formatter for unit-dependent display.
 */
export function buildCountChips(
  obj: Record<string, any>,
  prefix = "",
  labelMap: Record<string, string> = {},
  formatArea?: (m2: number) => string,
): IFeatureChip[] {
  if (!obj) return [];
  const chips: IFeatureChip[] = [];

  for (const [key, value] of Object.entries(obj)) {
    const v = value as Record<string, any>;
    if (!v || typeof v !== "object") continue;

    const overridePath = prefix ? `${prefix}.${key}` : key;
    const baseLabel = labelMap[key] ?? FIELD_OVERRIDES[overridePath]?.label ?? pathToLabel(key);

    if ("count" in v) {
      // Simple element: { count, area? }
      const count: number | null = v.count ?? null;
      const area: number | null = v.area ?? null;
      const detected = !!count || !!area;
      const detail =
        area != null && formatArea ? formatArea(area) : count != null ? String(count) : undefined;
      chips.push({ label: baseLabel, detected, count, area, detail });
    } else {
      // Nested subtypes: { photovoltaic: { count, area }, thermal: { count, area } }
      for (const [subKey, subValue] of Object.entries(v)) {
        const sv = subValue as Record<string, any>;
        if (!sv || typeof sv !== "object") continue;
        const count: number | null = sv.count ?? null;
        const area: number | null = sv.area ?? null;
        const detected = !!count || !!area;
        const detail =
          area != null && formatArea ? formatArea(area) : count != null ? String(count) : undefined;
        chips.push({ label: baseLabel, type: pathToLabel(subKey), detected, count, area, detail });
      }
    }
  }

  return chips;
}
